import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldArray, useFormikContext } from 'formik';

import { ReactComponent as AddButton } from '../../../../../assets/images/AddButton.svg';
import { ReactComponent as CrossIcon } from '../../../../../assets/images/CrossIcon.svg';
import { ReactComponent as PhoneIcon } from '../../../../../assets/images/PhoneIcon.svg';
import CustomPhoneInput from '../../../../../components/shared/CustomPhoneInput/CustomPhoneInput';
import { CONTACT_PHONE_TYPES } from '../../../../../constants/commonConstants';
import { filterData } from '../../../../../helpers/commonHelper';
import ContactFormSelect from '../ContactFormSelect/ContactFormSelect';

import styles from '../AddContactModal.module.scss';

const ContactFormPhoneNumberWeb = ({ isEdit }) => {
  const { t } = useTranslation('common');
  const { values, setValues } = useFormikContext();

  const lastElement = useMemo(() => {
    const filteredData = filterData(values.user_contact_phone_numbers_attributes);
    return filteredData[filteredData.length - 1];
  }, [values]);

  const handlePhoneInput = (data, index, arrayHelpers) => {
    const newData = [...values.user_contact_phone_numbers_attributes];
    const body = { ...newData[index], phone_numbers_attributes: { full_number: data } };
    arrayHelpers.replace(index, body);
  };

  const handleRemoveField = useCallback(
    (arrayHelpers, index) => {
      if (values.user_contact_phone_numbers_attributes.length === 1) {
        arrayHelpers.replace(index, {
          number_type: '',
          phone_numbers_attributes: { full_number: '' },
        });
      } else {
        arrayHelpers.remove(index);
      }
    },
    [values]
  );

  const handleEditRemoveField = useCallback(
    (arrayHelpers, index) => {
      const newData = [...values.user_contact_phone_numbers_attributes];
      if (newData[index].id) {
        newData[index] = { ...newData[index], _destroy: true };
        setValues({ ...values, user_contact_phone_numbers_attributes: newData });
      }
      const data = filterData(newData);
      if (data.length === 0) {
        arrayHelpers.insert(index + 1, {
          number_type: '',
          phone_numbers_attributes: { full_number: '' },
        });
      }
      if (!newData[index].id) {
        if (data.length === 1) {
          arrayHelpers.replace(index, {
            number_type: '',
            phone_numbers_attributes: { full_number: '' },
          });
        } else {
          arrayHelpers.remove(index);
        }
      }
    },
    [values]
  );

  const handleTypeChange = useCallback(
    (option, index, arrayHelpers) => {
      const newData = [...values.user_contact_phone_numbers_attributes];
      newData[index] = { ...newData[index], number_type: option.label };
      arrayHelpers.replace(index, newData[index]);
    },
    [values]
  );

  return (
    <div>
      <FieldArray
        name="user_contact_phone_numbers_attributes"
        render={(arrayHelpers) => (
          <div className={styles.container}>
            {values.user_contact_phone_numbers_attributes?.map((value, index) => {
              const { number_type, phone_numbers_attributes, _destroy } = value;
              if (!_destroy) {
                return (
                  <div className={styles.multiInputWrapper}>
                    <div className={styles.multiInputContainer}>
                      <div className={styles.multiInputField}>
                        <Field
                          name={`user_contact_phone_numbers_attributes[${index}].phone_numbers_attributes.full_number`}
                          as={CustomPhoneInput}
                          value={phone_numbers_attributes.full_number}
                          placeholder={t('placeholders.phone')}
                          handleChange={(data) => handlePhoneInput(data, index, arrayHelpers)}
                          CustomIcon={PhoneIcon}
                        />
                      </div>
                      {phone_numbers_attributes.full_number.length > 0 && (
                        <div className={styles.multiSelectField}>
                          <Field
                            name={`user_contact_phone_numbers_attributes[${index}].number_type`}
                            as={ContactFormSelect}
                            data={number_type}
                            placeholder={t('placeholders.label')}
                            options={CONTACT_PHONE_TYPES}
                            handleOnChange={(option) => handleTypeChange(option, index, arrayHelpers)}
                          />
                        </div>
                      )}
                    </div>
                    <div
                      className={
                        phone_numbers_attributes.full_number.length > 0
                          ? styles.showIconButtonContainer
                          : styles.iconButtonContainer
                      }
                    >
                      <button
                        type="button"
                        onClick={() =>
                          isEdit ? handleEditRemoveField(arrayHelpers, index) : handleRemoveField(arrayHelpers, index)
                        }
                        className={styles.iconButton}
                      >
                        <CrossIcon />
                      </button>
                      {phone_numbers_attributes.full_number.length > 0 &&
                        lastElement === values.user_contact_phone_numbers_attributes[index] && (
                          <button
                            className={styles.iconButton}
                            type="button"
                            onClick={() =>
                              arrayHelpers.insert(index + 1, {
                                number_type: '',
                                phone_numbers_attributes: { full_number: '' },
                              })
                            }
                          >
                            <AddButton />
                          </button>
                        )}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      />
    </div>
  );
};

export default ContactFormPhoneNumberWeb;
